/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, Drawer, Form, Input, Menu, Modal, Radio, Select, Space} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {patch, post, del, get} from "../core/request";
import {IconSet} from "../components/icons";
import {Dropdown} from "antd";
import {CustomButton} from "../components/buttons";

import {Customer, Device, DeviceType, Person, Todo} from "../core/datamodel";
import TextArea from "antd/es/input/TextArea";

type Class = Todo
const stack_selector = (state: any) => state.drawer.todo
const uri = "todos"
const key = "todo"
const title = "Todo"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function TodoDrawer(props: DrawerProps) {
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const customerReloadSignal = useAppSelector(state => state.reloadSignals.customer)
    const [customers, setCustomers] = useState([]);

    const deviceReloadSignal = useAppSelector(state => state.reloadSignals.device)
    const [devices, setDevices] = useState([]);

    const personId = useAppSelector(state => state.auth?.userInfo?.person_id);

    const [todoType, setTodoType] = useState< "mpe" | "customer" | "device" >("mpe");

    const isNew = () => !(!!object?.props?.uuid)


    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        onChangeCustomer(object?.props?.customer?.props?.uuid)
        form.resetFields()
        form.setFieldsValue({
            ...object?.props,
            customer: object?.props?.customer ? object?.props?.customer.props.uuid : undefined,
            device: object?.props?.device ? object?.props?.device.props.uuid : undefined,
        })
        setObject(object)
    }, [stack, form])

    useEffect(() => {
        get("customers").then(
            (response) => setCustomers(response.data.customers),
            (e) => console.error(e)
        )
    }, [stack, customerReloadSignal])

    useEffect(() => {
        if(object?.props?.device) {
            setTodoType("device")
        } else if (object?.props?.customer) {
            setTodoType("customer")
        } else {
            setTodoType("mpe")
        }

    }, [object])

    useEffect(() => {
        console.log("todo type", todoType)
    }, [todoType])


    const onFormFinish = () => {
        let values = form.getFieldsValue()

        object.update({
            ...values,
            customer: values.customer ? new Customer({'uuid': values.customer}) : null,
            device: values.device ? new Device({'uuid': values.device}) : null,
            user: personId && !values.customer && !values.device ? new Person({'uuid': personId}) : null,
            todo_type: values.device ? "device_free_text" : values.customer ? "customer_free_text" : "person_free_text",
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onChangeCustomer = (customer: any) => {

            console.debug("onChangeCustomer", customer)

            if (!customer) {
                setDevices([]);
                form.setFieldValue("device", undefined)
            }

            get("customers/" + customer + "/devices").then(
                (response) => setDevices(response.data.devices),
                (e) => console.error(e)
            )
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const customerOptions = (
        <>
            {
                customers.map(
                    (customer: { [key: string]: string }) =>
                        <Select.Option
                            key={customer.uuid}
                            value={customer.uuid}
                        >
                            {customer.repr}
                        </Select.Option>)
            }
        </>
    )

    const deviceOptions = (
        <>
            {
                devices.map(
                    (device: { [key: string]: string }) =>
                        <Select.Option
                            key={device.uuid}
                            value={device.uuid}
                        >
                            {device.repr}
                        </Select.Option>)
            }
        </>
    )

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item label="Beschreibung" name="comment" required rules={[{required: true, message: "Bitte Beschreibung eingeben!"}]}>
                <TextArea placeholder="Beschreibung"/>
            </Form.Item>

            {isNew() && (
                <Form.Item label={"anlegen für"}>
                    <Radio.Group
                        optionType={"button"}
                        buttonStyle={"solid"}
                        defaultValue={"mpe"}
                        onChange={(e) => setTodoType(e.target.value)}
                        options={
                            [
                                {label: "MPE", value: "mpe"},
                                {label: "Kunde", value: "customer"},
                                {label: "Gerät", value: "device"},
                            ]
                        }
                    />
                </Form.Item>
            )}

            { ["customer", "device"].includes(todoType)  && (
                <Form.Item label="Kunde" name="customer" required>
                    <Select placeholder={"Kunde wählen"} allowClear onChange={onChangeCustomer}>
                        {customerOptions}
                    </Select>
                </Form.Item>
            )}

            { todoType === "device" && (
                <Form.Item label="Gerät" name="device" required>
                    <Select placeholder={"Gerät wählen"} disabled={devices.length === 0} allowClear >
                        {deviceOptions}
                    </Select>
                </Form.Item>
            )}


            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "delete",
                            danger: true,
                            onClick: onDelete,
                            icon: <IconSet type='delete'/>,
                            label: "Löschen"
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {form_element}
        </Drawer>
    )
}


TodoDrawer.defaultProps = {
    size: 'large'
}